import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Camera, ImagePlus, Search, X, CheckCircle2, AlertTriangle, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { EquipmentSourcePicker } from '../equipment/EquipmentSourcePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WORK_OPTIONS = [
  { key: 'installed_new', label: 'Installed New' },
  { key: 'replaced_existing', label: 'Replaced Existing' },
  { key: 'repaired_serviced', label: 'Repaired / Serviced Existing' },
  { key: 'inspected', label: 'Inspected Existing' },
  { key: 'removed', label: 'Removed' },
  { key: 'identified', label: 'Identified / Added to Record' },
  { key: 'other', label: 'Other / Not Applicable' },
];
const USES_EXISTING = new Set(['replaced_existing', 'repaired_serviced', 'inspected', 'removed']);

// Upload a captured photo to the canonical job_media store, return its id.
async function uploadMedia(file, { appointmentId, customerId, workOrderNumber, category }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('category', category);
  if (appointmentId) fd.append('appointment_id', appointmentId);
  if (customerId) fd.append('customer_id', customerId);
  if (workOrderNumber) fd.append('work_order_number', workOrderNumber);
  const { data } = await axios.post(`${API}/job-media`, fd, {
    withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

const PhotoRow = ({ label, required, category, ctx, media, onChange }) => {
  const [busy, setBusy] = useState(false);
  const handleFiles = async (files) => {
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        const m = await uploadMedia(f, { ...ctx, category });
        added.push(m);
      }
      onChange([...(media || []), ...added]);
    } catch (e) {
      toast.error('Photo upload failed');
    }
    setBusy(false);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1">
        {label} {required && <span className="text-red-400">*</span>}
      </Label>
      <div className="flex flex-wrap gap-2">
        {(media || []).map((m) => (
          <div key={m.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-[#262626]" data-testid={`asset-photo-thumb-${category}`}>
            <img src={`${BACKEND_URL}${m.url}`} alt={category} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(media.filter((x) => x.id !== m.id))}
              className="absolute top-0 right-0 bg-black/70 text-white p-0.5" data-testid="asset-photo-remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {/* Two always-visible field-tech controls: shoot a new photo OR pick
            existing photos from the device. Both feed the same job_media pipeline.
            Side-by-side by default; stacks vertically on very narrow screens. */}
        <div className="flex flex-col min-[380px]:flex-row gap-2 w-full min-[380px]:w-auto">
          <label className="flex-1 min-[380px]:flex-none min-[380px]:w-24 h-16 rounded-md border border-dashed border-[#404040] flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] active:border-[#D4AF37] gap-0.5 px-2" data-testid={`asset-photo-take-${category}`}>
            <Camera className="w-5 h-5 text-[#737373]" />
            <span className="text-[10px] text-[#A3A3A3] leading-none text-center">Take Photo</span>
            <input type="file" accept="image/*" capture="environment" className="hidden"
              disabled={busy} onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} />
          </label>
          <label className="flex-1 min-[380px]:flex-none min-[380px]:w-24 h-16 rounded-md border border-dashed border-[#404040] flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] active:border-[#D4AF37] gap-0.5 px-2" data-testid={`asset-photo-upload-${category}`}>
            <ImagePlus className="w-5 h-5 text-[#737373]" />
            <span className="text-[10px] text-[#A3A3A3] leading-none text-center">Choose / Upload</span>
            <input type="file" accept="image/*" multiple className="hidden"
              disabled={busy} onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} />
          </label>
        </div>
      </div>
      {busy && <p className="text-[10px] text-[#D4AF37]" data-testid={`asset-photo-uploading-${category}`}>Uploading…</p>}
    </div>
  );
};

const AssetCard = ({ idx, asset, template, ctx, onPatch, onRemove }) => {
  const [dupResults, setDupResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const usesExisting = USES_EXISTING.has(asset.work_performed);
  const reqIds = template?.required_identifiers || [];
  const utc = asset.unable_to_capture || {};

  const searchExisting = async (q) => {
    if (!ctx.customerId) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/customers/${ctx.customerId}/assets`, { withCredentials: true });
      const filtered = (data || []).filter((a) => {
        if (asset.asset_type_key && a.type !== asset.asset_type_key) return false;
        if (!q) return true;
        const hay = `${a.brand} ${a.model} ${a.serial} ${a.location} ${a.asset_id_native || ''}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      });
      setDupResults(filtered);
    } finally { setSearching(false); }
  };

  useEffect(() => {
    if (usesExisting && ctx.customerId) searchExisting('');
    // eslint-disable-next-line
  }, [usesExisting, asset.asset_type_key]);

  const patchCustom = (k, v) => onPatch(idx, { custom_fields: { ...(asset.custom_fields || {}), [k]: v } });
  const patchWarranty = (k, v) => onPatch(idx, { warranty: { ...(asset.warranty || {}), [k]: v } });
  const setMedia = (category, media) => {
    const next = { ...(asset._media || {}), [category]: media };
    const allIds = Object.values(next).flat().map((m) => m.id);
    onPatch(idx, { _media: next, media_ids: allIds });
  };

  // Raw missing = required identifiers still blank, IGNORING the unable-to-capture
  // excuse set. Deriving the block's visibility from this (not the post-excused set)
  // keeps the reason input mounted after the tech taps "Unable to Capture".
  const rawMissing = reqIds.filter((f) => !String(asset[f] || '').trim());
  const isInstall = ['installed_new', 'replaced_existing'].includes(asset.work_performed);
  const needsAttention = isInstall && rawMissing.length > 0;
  const markedUnable = (utc.fields || []).length > 0;
  const reasonFilled = String(utc.reason || '').trim().length > 0;

  return (
    <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-3" data-testid={`asset-card-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="text-white text-sm font-semibold flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#D4AF37]" /> {template?.display_name || asset.asset_type_key || 'Asset'}
        </p>
        <button type="button" onClick={() => onRemove(idx)} className="text-[#737373] hover:text-red-400" data-testid={`asset-remove-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div>
        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Work Performed on Asset</Label>
        <Select value={asset.work_performed} onValueChange={(v) => onPatch(idx, { work_performed: v, existing_asset_id: USES_EXISTING.has(v) ? asset.existing_asset_id : null })}>
          <SelectTrigger data-testid={`asset-work-${idx}`} className="bg-[#141414] border-[#262626] text-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626] text-white">
            {WORK_OPTIONS.map((w) => <SelectItem key={w.key} value={w.key} data-testid={`asset-work-opt-${w.key}`}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {usesExisting && (
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><Search className="w-3 h-3" /> Select Existing Asset</Label>
          <Input placeholder="Search existing…" onChange={(e) => searchExisting(e.target.value)} className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-existing-search-${idx}`} />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {searching && <p className="text-[#737373] text-xs">Searching…</p>}
            {!searching && dupResults.length === 0 && <p className="text-[#737373] text-xs">No existing asset on file — capture it below (adds to record).</p>}
            {dupResults.map((a) => (
              <button key={a.id} type="button" onClick={() => {
                if (asset.work_performed === 'replaced_existing') {
                  // Store the OLD unit as a read-only snapshot and leave the NEW
                  // identity fields BLANK so the tech captures the new unit — never
                  // edits the old one into the new record.
                  onPatch(idx, { existing_asset_id: a.id,
                    _old_asset: { brand: a.brand, model: a.model, serial: a.serial, location: a.location },
                    brand: '', model: '', serial: '', location: a.location || '' });
                } else {
                  onPatch(idx, { existing_asset_id: a.id, brand: a.brand, model: a.model, serial: a.serial, location: a.location });
                }
              }}
                className={`w-full text-left px-2 py-1.5 rounded border text-xs ${asset.existing_asset_id === a.id ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}
                data-testid={`asset-existing-opt-${a.id}`}>
                {a.brand || '—'} {a.model || ''} · SN {a.serial || a.asset_id_native || '—'} · {a.location || 'no location'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Replacement callout — pins the OLD (retiring) unit read-only and directs
          the tech to enter the NEW unit below, so it never looks like editing old. */}
      {asset.work_performed === 'replaced_existing' && asset.existing_asset_id && (
        <div className="bg-[#141414] border border-[#D4AF37]/40 rounded-md px-3 py-2" data-testid={`asset-replacing-chip-${idx}`}>
          <p className="text-[10px] text-[#737373] uppercase tracking-wider">Replacing (old unit → will be retired)</p>
          <p className="text-[#A3A3A3] text-xs">{(asset._old_asset?.brand || '—')} {asset._old_asset?.model || ''} · SN {asset._old_asset?.serial || '—'}</p>
          <p className="text-[#D4AF37] text-[11px] font-semibold mt-1">↓ Enter the NEW replacement unit's details below</p>
        </div>
      )}

      {/* Core identity fields (creating/adding, or the NEW unit on a replacement) */}
      {(!usesExisting || !asset.existing_asset_id || asset.work_performed === 'replaced_existing') && (
        <div>
          {asset.work_performed === 'replaced_existing' && asset.existing_asset_id && (
            <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-bold mb-1" data-testid={`asset-new-unit-header-${idx}`}>New Replacement Unit</p>
          )}
          <div className="grid grid-cols-2 gap-2">
          {(template?.core_fields || ['brand', 'model', 'serial', 'install_date', 'location']).map((f) => (
            <div key={f} className={f === 'install_date' || f === 'location' ? 'col-span-2 sm:col-span-1' : ''}>
              <Label className="text-[10px] text-[#737373] uppercase tracking-wider">
                {f === 'brand' ? 'Manufacturer' : f.replace(/_/g, ' ')}
                {reqIds.includes(f) && <span className="text-red-400"> *</span>}
              </Label>
              <Input type={f === 'install_date' ? 'date' : 'text'} value={asset[f] || ''}
                onChange={(e) => onPatch(idx, { [f]: e.target.value })}
                className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-field-${f}-${idx}`} />
            </div>
          ))}
          {(template?.fields || []).map((f) => (
            <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : ''}>
              <Label className="text-[10px] text-[#737373] uppercase tracking-wider">{f.label}{f.required && <span className="text-red-400"> *</span>}</Label>
              {f.type === 'select' ? (
                <Select value={(asset.custom_fields || {})[f.key] || ''} onValueChange={(v) => patchCustom(f.key, v)}>
                  <SelectTrigger className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-custom-${f.key}-${idx}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] text-white">
                    {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={(asset.custom_fields || {})[f.key] || ''} onChange={(e) => patchCustom(f.key, e.target.value)}
                  className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-custom-${f.key}-${idx}`} />
              )}
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Warranty (only if the template supports it) */}
      {template?.warranty_enabled && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-[#1a1a1a]">
          {(template.warranty_fields || []).map((wf) => (
            <div key={wf.key}>
              <Label className="text-[10px] text-[#737373] uppercase tracking-wider">{wf.label}</Label>
              <Input type={wf.type === 'date' ? 'date' : 'text'} value={(asset.warranty || {})[wf.key] || ''}
                onChange={(e) => patchWarranty(wf.key, e.target.value)}
                className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-warranty-${wf.key}-${idx}`} />
            </div>
          ))}
        </div>
      )}

      {isInstall && <EquipmentSourcePicker idx={idx} asset={asset} onPatch={onPatch} />}

      {/* Template-required photos */}
      {(template?.photo_categories || []).map((pc) => (
        <PhotoRow key={pc.category} label={`${pc.category.replace(/_/g, ' ')} Photo`} required={pc.required}
          category={pc.category} ctx={ctx} media={(asset._media || {})[pc.category] || []}
          onChange={(m) => setMedia(pc.category, m)} />
      ))}

      {/* Unable to capture (surfaces when required identifiers are still missing) */}
      {needsAttention && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-md p-2 space-y-1.5" data-testid={`asset-unable-${idx}`}>
          <p className="text-red-300 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Missing required: {rawMissing.join(', ')}. Enter it above, or mark Unable to Capture with a reason.</p>
          {!markedUnable && (
            <button type="button" onClick={() => onPatch(idx, { unable_to_capture: { fields: rawMissing, reason: '' } })}
              className="text-xs px-2 py-1 rounded border border-red-500/50 text-red-300" data-testid={`asset-unable-btn-${idx}`}>Unable to Capture</button>
          )}
          {markedUnable && (
            <div className="space-y-1.5">
              <Input placeholder="Required reason (e.g. data plate unreadable)…" value={utc.reason || ''}
                onChange={(e) => onPatch(idx, { unable_to_capture: { fields: rawMissing, reason: e.target.value } })}
                className="bg-[#141414] border-red-500/40 text-white h-9" data-testid={`asset-unable-reason-${idx}`} />
              <div className="flex items-center justify-between">
                <span className={`text-[11px] ${reasonFilled ? 'text-green-400' : 'text-red-300'}`} data-testid={`asset-unable-status-${idx}`}>
                  {reasonFilled ? 'Reason recorded — you can complete the job.' : 'A reason is required to proceed.'}
                </span>
                <button type="button" onClick={() => onPatch(idx, { unable_to_capture: null })}
                  className="text-[11px] text-[#737373] hover:text-white" data-testid={`asset-unable-cancel-${idx}`}>Undo</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Notes</Label>
        <Input value={asset.notes || ''} onChange={(e) => onPatch(idx, { notes: e.target.value })}
          className="bg-[#141414] border-[#262626] text-white h-9" data-testid={`asset-notes-${idx}`} />
      </div>
    </div>
  );
};

/**
 * Generic "Assets / Systems Worked On" step. Progressive disclosure: shows a
 * single "No Asset / System" fast path until the tech adds an asset. Driven
 * entirely by the service type's asset_capture rule + asset_type_templates —
 * nothing plumbing-specific is hardcoded here.
 *
 * Writes captured assets into the parent `equipment` state via setEquipment.
 */
const AssetSystemsStep = ({ serviceType, appointment, equipment, setEquipment, onValidityChange }) => {
  const [templates, setTemplates] = useState([]);
  const [captureRule, setCaptureRule] = useState({ mode: 'none', suggested_types: [], allow_multiple: true });
  const [noAsset, setNoAsset] = useState(false);
  const [beforeMedia, setBeforeMedia] = useState([]);
  const [afterMedia, setAfterMedia] = useState([]);

  const ctx = useMemo(() => ({
    appointmentId: appointment?.id, customerId: appointment?.customer_id,
    workOrderNumber: appointment?.work_order_number,
  }), [appointment]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tpl, st] = await Promise.all([
          axios.get(`${API}/asset-type-templates`, { withCredentials: true }),
          axios.get(`${API}/service-types`, { withCredentials: true }),
        ]);
        if (!alive) return;
        setTemplates(tpl.data || []);
        const match = (st.data || []).find((s) => s.name === serviceType);
        const rule = match?.asset_capture || { mode: 'none', suggested_types: [], allow_multiple: true };
        setCaptureRule(rule);
        // Auto-seed suggested assets for required/recommended service types.
        if ((rule.mode === 'required' || rule.mode === 'recommended') && (equipment || []).length === 0) {
          const seeded = (rule.suggested_types || []).slice(0, 1).map((key) => newAsset(key));
          if (seeded.length) setEquipment(seeded);
        }
      } catch (e) { /* non-blocking */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [serviceType]);

  const tplByKey = useMemo(() => {
    const m = {}; templates.forEach((t) => { m[t.key] = t; }); return m;
  }, [templates]);

  function newAsset(key) {
    return { asset_type_key: key, work_performed: 'installed_new', custom_fields: {}, warranty: {}, media_ids: [], _media: {}, equipment_source: '', quantity: 1 };
  }
  const addAsset = (key) => { setNoAsset(false); setEquipment([...(equipment || []), newAsset(key)]); };
  const removeAsset = (i) => setEquipment((equipment || []).filter((_, j) => j !== i));
  const patchAsset = (i, patch) => setEquipment((equipment || []).map((a, j) => (j === i ? { ...a, ...patch } : a)));

  const suggested = captureRule.suggested_types || [];
  const canAddMore = captureRule.allow_multiple !== false || (equipment || []).length === 0;

  // Report a blocking validation message to the wizard (completion cannot proceed
  // until every install/replacement has its required identifiers or an explicit
  // "Unable to Capture" reason; a "required" service type needs at least one asset).
  useEffect(() => {
    if (!onValidityChange) return;
    let err = null;
    if (captureRule.mode === 'required' && (equipment || []).length === 0) {
      err = 'This service type requires capturing at least one asset/system.';
    }
    for (const a of (equipment || [])) {
      const t = tplByKey[a.asset_type_key];
      if (!t) continue;
      if (!['installed_new', 'replaced_existing'].includes(a.work_performed)) continue;
      if (!a.equipment_source) { err = `${t.display_name}: choose the Equipment Source (Van Stock, Shop / Warehouse, Supplier Pickup, Customer Supplied or Other / Unknown).`; break; }
      const reqIds = t.required_identifiers || [];
      const utc = a.unable_to_capture || {};
      const reason = String(utc.reason || '').trim();
      const excused = reason ? new Set(utc.fields || []) : new Set();
      const missing = reqIds.filter((f) => !String(a[f] || '').trim() && !excused.has(f));
      if (missing.length) {
        err = `${t.display_name}: ${missing.join(', ')} required — enter it or mark "Unable to Capture" with a reason.`;
        break;
      }
    }
    onValidityChange(err);
  }, [equipment, tplByKey, captureRule, onValidityChange]);

  return (
    <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-3" data-testid="asset-systems-step">
      <div className="flex items-center justify-between">
        <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Assets / Systems Worked On</p>
        {captureRule.mode === 'required' && <span className="text-[10px] text-red-300 font-bold uppercase">Required</span>}
      </div>

      {(equipment || []).length === 0 && !noAsset && captureRule.mode !== 'required' && (
        <button type="button" onClick={() => { setNoAsset(true); setEquipment([]); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#404040] text-sm"
          data-testid="asset-no-asset-btn">
          <CheckCircle2 className="w-4 h-4" /> No Asset / System Worked On
        </button>
      )}
      {noAsset && (
        <div className="flex items-center justify-between px-3 py-2 rounded-md bg-[#141414] border border-[#262626]" data-testid="asset-no-asset-selected">
          <span className="text-[#A3A3A3] text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> No asset / system worked on</span>
          <button type="button" onClick={() => setNoAsset(false)} className="text-[#737373] hover:text-white text-xs">Undo</button>
        </div>
      )}

      {(equipment || []).map((asset, i) => (
        <AssetCard key={i} idx={i} asset={asset} template={tplByKey[asset.asset_type_key]} ctx={ctx}
          onPatch={patchAsset} onRemove={removeAsset} />
      ))}

      {!noAsset && canAddMore && (
        <div className="flex flex-wrap gap-2">
          {suggested.map((key) => (
            <button key={key} type="button" onClick={() => addAsset(key)}
              className="text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-1"
              data-testid={`asset-suggest-${key}`}>
              <Plus className="w-3 h-3" /> {tplByKey[key]?.display_name || key}
            </button>
          ))}
          <AddOther templates={templates} onAdd={addAsset} />
        </div>
      )}

      {/* Structured Before / After job photos — tied to the job for invoice + portal */}
      <div className="pt-2 border-t border-[#1a1a1a] grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PhotoRow label="Before Photos" category="before" ctx={ctx} media={beforeMedia} onChange={setBeforeMedia} />
        <PhotoRow label="After Photos" category="after" ctx={ctx} media={afterMedia} onChange={setAfterMedia} />
      </div>
      <p className="text-[#525252] text-[10px]">Before/After photos attach to this job automatically and appear on the invoice; customer-safe photos show in the customer portal.</p>
    </div>
  );
};

const AddOther = ({ templates, onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="text-xs px-3 py-1.5 rounded-full border border-[#262626] text-[#A3A3A3] hover:text-white flex items-center gap-1"
        data-testid="asset-add-other">
        <Plus className="w-3 h-3" /> Add Another Asset/System
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-56 max-h-60 overflow-y-auto bg-[#141414] border border-[#262626] rounded-md p-1 shadow-xl">
          {templates.map((t) => (
            <button key={t.key} type="button" onClick={() => { onAdd(t.key); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 rounded text-xs text-[#A3A3A3] hover:text-white hover:bg-[#1a1a1a]"
              data-testid={`asset-other-opt-${t.key}`}>
              {t.display_name} <span className="text-[#525252]">· {t.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssetSystemsStep;
